import { BillingSystemStore } from '../stores/billingSystem';
import { addNotification, setUiTotal } from './ui';
import { IBodyItemResponse, IBodyListResponse, IGetApiResponse, IRequestAPIBody, apiManager } from '../REST';
import {
    BillingOrderProduct,
    IBillingSystemCompanySelectAPI,
    IBillingSystemCompanySelectItem,
    IBillingSystemOrdersListAPI,
    IBillingSystemOrdersListItem,
    IBillingSystemPostInvoiceNumberBody,
    IGeneralNonRecurringChargesAPI,
    IGetOrder,
    IGetOrderAPI,
    INonRecurringCharges,
    INonRecurringChargesItem,
    IParamsList,
    IPostBillingOrder,
    IPostBillingOrderAPI,
    IPostItem,
    InvoiceSendEmailResp,
    NotificationType,
    billingSystemOrderStatuses,
} from '../interfaces';
import { mapApi2UiField, mapUi2ApiField, sortByName } from '../instruments';

export function setBillingSystemForcedUpdateTime(): void {
    const BillingSystemStoreInstance = BillingSystemStore.getInstance();
    BillingSystemStoreInstance.setForcedUpdateTime();
}

export function cleanBillingSystemStore(): void {
    const StoreInstance = BillingSystemStore.getInstance();
    StoreInstance.cleanStore();
}

export function setBillingSystemOrdersList(apiData: IBillingSystemOrdersListAPI): void {
    const storeData: IBillingSystemOrdersListItem[] = apiData.orders?.map((order) => {
        return {
            ...mapApi2UiField(order || {}),
        };
    }) as IBillingSystemOrdersListItem[];

    const BillingSystemStoreInstance = BillingSystemStore.getInstance();
    BillingSystemStoreInstance.setOrdersList(storeData);
}

export async function getBillingSystemOrdersList(params: IParamsList): Promise<number> {
    const { resp } = await apiManager.getApi('GET_BILLING_SYSTEM_ORDERS', { searchParamsList: params });
    const { meta } = resp as IBodyListResponse;
    meta && setUiTotal(meta.records_count);
    resp && setBillingSystemOrdersList(resp as IBillingSystemOrdersListAPI);
    return Number(meta?.records_count);
}

export async function patchBillingStatusOrder(id: string, status: billingSystemOrderStatuses): Promise<IPostItem> {
    const body = {
        status,
    } as IRequestAPIBody;

    const { isError, resp } = await apiManager.getApi('PATCH_BILLING_SYSTEM_ORDER', { id }, body);
    const response = resp as IBodyItemResponse;
    const { errors } = response;

    return { isError, errors, resp: response };
}

export async function putBillingSystemOrder(id: string, order: IPostBillingOrder): Promise<IPostItem> {
    const { products, discount, paymentType, ...attributes } = order;
    const body: IPostBillingOrderAPI = {
        order: {
            ...mapUi2ApiField(attributes),
            payment_type: paymentType,
            products: products?.map((p) => ({ ...mapUi2ApiField(p) })),
        },
    };
    if (discount?.amount || discount?.amountType || discount?.description) {
        body.order.discount = { ...mapUi2ApiField(order?.discount || {}) };
    }

    const { resp, isError } = await apiManager.getApi('PUT_BILLING_SYSTEM_ORDER', { id }, body as IRequestAPIBody);

    const response = resp as IBodyItemResponse;
    const { errors } = response;

    return { isError, errors, resp: response };
}

export async function getBillingSystemOrder(id: string): Promise<IGetOrder> {
    const { resp, isError } = await apiManager.getApi('GET_BILLING_SYSTEM_ORDER', { id });
    const orderResponse = resp as IGetOrderAPI;
    const order = mapApi2UiField(orderResponse.order || {});

    order.products = order?.products?.map((product: BillingOrderProduct) => {
        return {
            ...mapApi2UiField(product || {}),
        };
    });

    order.discount = mapApi2UiField(order?.discount || {});
    order.invoice = mapApi2UiField(order?.invoice || {});

    return {
        isError,
        order,
    };
}

export async function deleteBillingSystemOrder(id: string): Promise<{ isError: boolean }> {
    const { isError } = await apiManager.getApi('DELETE_BILLING_SYSTEM_ORDER', { id });
    return { isError };
}

export async function postBillingSystemOrderApprove(id: string, body?: IRequestAPIBody): Promise<{ isError: boolean }> {
    const { isError } = await apiManager.getApi('POST_BILLING_SYSTEM_ORDER_APPROVE', { id }, body);
    return { isError };
}

export async function postBillingSystemInvoiceSendEmail(id: string): Promise<void> {
    const { isError, errors, resp } = await apiManager.getApi('POST_INVOICE', { id });

    if (isError) {
        addNotification({
            type: NotificationType.ERROR,
            message: `Invoice Send Email id: ${id}`,
            isUsersError: true,
            isTranslated: true,
            description: JSON.stringify(errors),
        });
    } else {
        const rrr = resp as InvoiceSendEmailResp;

        addNotification({
            type: NotificationType.SUCCESS,
            message: `Invoice Send Email id: ${id}`,
            isUsersError: true,
            isTranslated: true,
            description: `Job id - ${rrr.job_id}, Successfully enqueued - ${rrr.successfully_enqueued}`,
            duration: 15,
        });
    }
}

export async function postBillingSystemOrder(order: IPostBillingOrder): Promise<IPostItem> {
    const { products, discount, paymentType, statusManual, ...attributes } = order;
    const body: IPostBillingOrderAPI = {
        order: {
            ...mapUi2ApiField(attributes),
            payment_type: paymentType,
            products: products?.map((p) => ({ ...mapUi2ApiField(p) })),
        },
    };
    if (statusManual) {
        (body.order as any).status_manual = statusManual;
    }
    if (discount?.amount || discount?.amountType || discount?.description) {
        body.order.discount = { ...mapUi2ApiField(order?.discount || {}) };
    }

    const { resp, isError } = await apiManager.getApi('POST_BILLING_SYSTEM_ORDER', {}, body as IRequestAPIBody);

    const response = resp as IBodyItemResponse;
    const { errors } = response;

    return { isError, errors, resp: response };
}

export async function getGeneralNonRecurringChargesList(): Promise<INonRecurringCharges> {
    const { resp } = await apiManager.getApi('GET_BILLING_SYSTEM_NON_RECURRING_PAYMENTS', {});
    const api = resp as IGeneralNonRecurringChargesAPI;
    return (
        api?.general_non_recurring_charges?.map((i) => ({
            id: i.id || '',
            name: i.name,
            amountCents: i.amount_cents / 100,
            predefinedType: i.predefined_type,
        })) || []
    );
}

export async function postGeneralNonRecurringCharge(values: INonRecurringChargesItem): Promise<IPostItem> {
    const body = { name: values.name, amount_cents: values.amountCents * 100 } as IRequestAPIBody;
    const { resp, isError } = await apiManager.getApi('POST_BILLING_SYSTEM_NON_RECURRING_PAYMENTS', {}, body);

    const response = resp as IBodyItemResponse;

    return { isError, resp: response };
}

export async function putGeneralNonRecurringCharge(
    id: string | number,
    values: INonRecurringChargesItem,
): Promise<IPostItem> {
    const body = {
        name: values.name,
        amount_cents: values.amountCents * 100,
    } as IRequestAPIBody;
    const { resp, isError } = await apiManager.getApi(
        'PUT_BILLING_SYSTEM_NON_RECURRING_PAYMENTS',
        { id: `${id}` },
        body,
    );

    const response = resp as IBodyItemResponse;

    return { isError, resp: response };
}

export async function deleteGeneralNonRecurringCharge(id: string | number): Promise<{ isError: boolean }> {
    const { isError } = await apiManager.getApi('DELETE_BILLING_SYSTEM_NON_RECURRING_PAYMENTS', { id: `${id}` });
    return { isError };
}

export function setBillingCompaniesSelect(data: IBillingSystemCompanySelectAPI) {
    const BillingSystemStoreInstance = BillingSystemStore.getInstance();
    const select = data.companies
        ?.filter((i) => i.deleted_at === null)
        ?.map((i) => {
            const item = mapApi2UiField(i) as IBillingSystemCompanySelectItem;

            return { ...item, value: item.id, label: item.name };
        })
        .sort(sortByName) as IBillingSystemCompanySelectItem[];

    BillingSystemStoreInstance.setCompaniesSelect(select);
}

export async function getBillingCompaniesSelect(options?: {
    isInvoicing?: boolean;
    isInvoiceable?: boolean;
}): Promise<void> {
    const { isInvoicing, isInvoiceable } = options || {};
    const { resp } = await apiManager.getApi('GET_BILLING_SYSTEM_COMPANIES_SELECT', {
        searchParamsList: {
            withoutMno: true,
            companyInvoice: !!isInvoicing,
            invoiceable: !!isInvoiceable,
        },
    });

    const selectData = resp as IBillingSystemCompanySelectAPI;

    selectData && setBillingCompaniesSelect(selectData);
}

export async function getBillingCompaniesInvoiceNumber({
    formatedDate,
    companyId,
}: {
    formatedDate: string;
    companyId: string;
}): Promise<IGetApiResponse> {
    const { resp, isError } = await apiManager.getApi('GET_BILLING_SYSTEM_INVOICE_NUMBER', {
        searchParamsList: {
            invoiceDate: formatedDate,
        },
        id: companyId,
    });

    const response = resp as IBodyItemResponse;
    const { errors } = response;

    return { isError, errors, resp: response };
}

export async function postBillingCompaniesInvoiceNumber(
    body: IBillingSystemPostInvoiceNumberBody,
    companyId: string,
    isPut: boolean,
): Promise<IGetApiResponse> {
    const parsedBody = body as IRequestAPIBody;

    const { resp, isError } = await apiManager.getApi(
        isPut ? 'PUT_BILLING_SYSTEM_INVOICE_NUMBER' : 'POST_BILLING_SYSTEM_INVOICE_NUMBER',
        { companyId },
        parsedBody,
    );

    const response = resp as IBodyItemResponse;
    const { errors } = response;

    return { isError, errors, resp: response };
}

export async function deleteBillingCompaniesInvoiceNumber(companyId: string): Promise<IGetApiResponse> {
    const { resp, isError } = await apiManager.getApi('DELETE_BILLING_SYSTEM_INVOICE_NUMBER', { companyId });

    const response = resp as IBodyItemResponse;
    const { errors } = response;

    return { isError, errors, resp: response };
}
